import {
  AdsClick,
  AutoAwesome,
  Check,
  Close,
  Delete,
  Download,
  ExpandMore,
  FirstPage,
  HelpOutline,
  History,
  LastPage,
  NavigateBefore,
  NavigateNext,
  PersonSearch,
  Refresh,
  Save,
  Search,
} from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Slider,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { debounce } from "@mui/material/utils";
import axios from "axios";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import LoadingButton from "../../../components/LoadingButton";
import { hostname, parseError } from "../../../utils";
import {
  commonCities,
  commonIndustries,
  companyRevenueOptions,
  contactNotNullsOptions,
  craCodeOptions,
  creditCardUserOptions,
  creditRatingOptions,
  departmentOptions,
  dwellingTypeOptions,
  educationOptions,
  employeeCountOptions,
  estimatedHomeValueOptions,
  ethnicityOptions,
  genderOptions,
  incomeRangeOptions,
  investmentOptions,
  languageOptions,
  mortgageAmountOptions,
  naicsCodeOptions,
  netWorthOptions,
  newCreditRangeOptions,
  occupationGroupOptions,
  seniorityOptions,
  sicCodeOptions,
  smokerOptions,
  usStates,
  yesNoOptions,
} from "./audienceBuilderFilterOptions";
import PreviousAudiencesDialog from "./PreviousAudiencesDialog";
import RefreshSettingsDialog from "./RefreshSettingsDialog";
import SegmentSearchDialog from "./SegmentSearchDialog";

interface AudienceBuilderDialogProps {
  open: boolean;
  onClose: () => void;
  getToken: () => Promise<string>;
  eventListener: (event: any) => void;
  companyId: string;
  listId: string;
  listName?: string;
  onSuccess?: () => void;
  onBackgroundTaskCreated?: () => void;
  openDialog: (
    title: string,
    content: string,
    buttonText: string,
    onClick: () => void
  ) => void;
}

// List audience type (from our database)
interface ListAudience {
  id: string;
  companyId: string;
  listId: string;
  audienceId: string;
  audienceName: string;
  status: string;
  refreshInterval: string | null;
  maxRecordsPerRefresh: number | null;
  createdDate: string;
  updatedDate: string | null;
}

// Preview contact type
interface PreviewContact {
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
  positionTitle?: string;
  companyName?: string;
  location?: string;
  linkedinUrl?: string;
  [key: string]: any;
}

// Polling configuration
const MAX_POLL_ATTEMPTS = 60; // 60 attempts * 3 seconds = 180 seconds (3 minutes) max wait

const AudienceBuilderDialog: React.FC<AudienceBuilderDialogProps> = ({
  open,
  onClose,
  getToken,
  eventListener,
  companyId,
  listId,
  listName,
  onSuccess,
  onBackgroundTaskCreated,
  openDialog,
}) => {
  // Loading states
  const [loading, setLoading] = useState(false);
  const [loadingAudience, setLoadingAudience] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [refreshPreviewLoading, setRefreshPreviewLoading] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [loadingAttributes, setLoadingAttributes] = useState(false);

  // Status message
  const [statusMessage, setStatusMessage] = useState<{
    type: "success" | "error" | "info" | "warning";
    message: string;
  } | null>(null);

  // Existing audience for this list
  const [listAudience, setListAudience] = useState<ListAudience | null>(null);

  // Preview data
  const [previewContacts, setPreviewContacts] = useState<PreviewContact[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(25);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [hasPreview, setHasPreview] = useState(false);
  const [audienceStatus, setAudienceStatus] = useState<string>("");
  const [pollingForStatus, setPollingForStatus] = useState(false);
  const [previewAudienceId, setPreviewAudienceId] = useState<string | null>(
    null
  );
  const [isSaved, setIsSaved] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);

  // Import size selection
  const [importSize, setImportSize] = useState<string>("all");

  // Refresh settings
  const [refreshInterval, setRefreshInterval] = useState<string>("none");
  const [maxRecordsPerRefresh, setMaxRecordsPerRefresh] = useState<
    number | null
  >(null);
  const [refreshSettingsDialogOpen, setRefreshSettingsDialogOpen] = useState(false);

  // Email notification settings
  const [emailNotificationEnabled, setEmailNotificationEnabled] = useState(false);
  const [emailNotificationRecipients, setEmailNotificationRecipients] = useState<string[]>([]);
  const [emailNotificationFormat, setEmailNotificationFormat] = useState<string>("csv");

  // Dynamic attribute options from API
  const [industryOptions, setIndustryOptions] =
    useState<string[]>(commonIndustries);

  // Segment search state
  const [segmentOptions, setSegmentOptions] = useState<
    { id: number; name: string }[]
  >([]);
  const [segmentSearchQuery, setSegmentSearchQuery] = useState("");
  const [loadingSegments, setLoadingSegments] = useState(false);
  const [selectedSegments, setSelectedSegments] = useState<
    { id: number; name: string }[]
  >([]);

  // Audience filters
  const [audienceName, setAudienceName] = useState("");
  const [cities, setCities] = useState<string[]>([]);
  const [states, setStates] = useState<string[]>([]);
  const [zipCodes, setZipCodes] = useState<string[]>([]);
  const [industries, setIndustries] = useState<string[]>([]);
  const [departments, setDepartments] = useState<string[]>([]);
  const [seniorities, setSeniorities] = useState<string[]>([]);
  const [genders, setGenders] = useState<string[]>([]);
  const [ageRange, setAgeRange] = useState<[number, number]>([18, 65]);
  const [useAgeFilter, setUseAgeFilter] = useState(false);
  const [daysBack, setDaysBack] = useState<number>(7);
  const [minimumScore, setMinimumScore] = useState<string>("");

  // Audience/Intent filters
  const [audienceType, setAudienceType] = useState<"premade" | "keyword">("premade");
  const [segmentSearches, setSegmentSearches] = useState<string[]>([]);

  // Profile filters
  const [incomeRanges, setIncomeRanges] = useState<string[]>([]);
  const [homeowner, setHomeowner] = useState<string[]>([]);
  const [married, setMarried] = useState<string[]>([]);
  const [netWorth, setNetWorth] = useState<string[]>([]);
  const [children, setChildren] = useState<string[]>([]);

  // Financial attribute filters (from Audience API attributes)
  const [creditRating, setCreditRating] = useState<string[]>([]);
  const [newCreditRange, setNewCreditRange] = useState<string[]>([]);
  const [creditCardUser, setCreditCardUser] = useState<string[]>([]);
  const [investment, setInvestment] = useState<string[]>([]);
  const [mortgageAmount, setMortgageAmount] = useState<string[]>([]);
  const [occupationGroup, setOccupationGroup] = useState<string[]>([]);
  const [craCode, setCraCode] = useState<string[]>([]);

  // Personal attribute filters (from Audience API attributes)
  const [ethnicity, setEthnicity] = useState<string[]>([]);
  const [language, setLanguage] = useState<string[]>([]);
  const [education, setEducation] = useState<string[]>([]);
  const [smoker, setSmoker] = useState<string[]>([]);

  // Home attribute filters (from Audience API attributes)
  const [dwellingType, setDwellingType] = useState<string[]>([]);
  const [homeYearBuiltMin, setHomeYearBuiltMin] = useState<string>("");
  const [homeYearBuiltMax, setHomeYearBuiltMax] = useState<string>("");
  const [homePurchasePriceMin, setHomePurchasePriceMin] = useState<string>("");
  const [homePurchasePriceMax, setHomePurchasePriceMax] = useState<string>("");
  const [homePurchaseYearMin, setHomePurchaseYearMin] = useState<string>("");
  const [homePurchaseYearMax, setHomePurchaseYearMax] = useState<string>("");
  const [estimatedHomeValue, setEstimatedHomeValue] = useState<string[]>([]);

  // Contact filters (notNulls)
  const [contactNotNulls, setContactNotNulls] = useState<string[]>([]);

  // Additional business profile filters
  const [jobTitles, setJobTitles] = useState<string[]>([]);
  const [companyNames, setCompanyNames] = useState<string[]>([]);
  const [companyDomains, setCompanyDomains] = useState<string[]>([]);
  const [employeeCounts, setEmployeeCounts] = useState<string[]>([]);
  const [companyRevenues, setCompanyRevenues] = useState<string[]>([]);
  const [sicCodes, setSicCodes] = useState<string[]>([]);
  const [naicsCodes, setNaicsCodes] = useState<string[]>([]);
  const [companyDescriptions, setCompanyDescriptions] = useState<string[]>([]);

  // AI keyword generator state (for business keywords)
  const [showKeywordGenerator, setShowKeywordGenerator] = useState(false);
  const [keywordGeneratorInput, setKeywordGeneratorInput] = useState("");
  const [generatingKeywords, setGeneratingKeywords] = useState(false);

  // AI intent keyword generator state
  const [showIntentKeywordGenerator, setShowIntentKeywordGenerator] = useState(false);
  const [intentKeywordGeneratorInput, setIntentKeywordGeneratorInput] = useState("");
  const [generatingIntentKeywords, setGeneratingIntentKeywords] = useState(false);

  // Segment search dialog state
  const [segmentSearchDialogOpen, setSegmentSearchDialogOpen] = useState(false);

  // Previous audiences dialog state
  const [previousAudiencesDialogOpen, setPreviousAudiencesDialogOpen] = useState(false);

  // Load existing audience when dialog opens
  useEffect(() => {
    if (open) {
      loadExistingAudience();
      loadAttributes();
    }
  }, [open, companyId, listId]);

  const loadExistingAudience = async () => {
    setLoadingAudience(true);
    try {
      const token = await getToken();
      const response = await axios.get(
        `${hostname}/lists/${listId}/audience/audience`,
        {
          params: { companyId },
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const audience = response.data.audience;
      if (audience) {
        setListAudience(audience);
        setAudienceName(audience.audienceName);
        setIsSaved(true);
        setPreviewAudienceId(null);

        // Initialize refresh settings
        setRefreshInterval(audience.refreshInterval || "none");
        setMaxRecordsPerRefresh(audience.maxRecordsPerRefresh);

        // Initialize email notification settings
        setEmailNotificationEnabled(audience.emailNotificationEnabled || false);
        setEmailNotificationRecipients(audience.emailNotificationRecipients || []);
        setEmailNotificationFormat(audience.emailNotificationFormat || "csv");

        // Populate filters from saved audience configuration
        if (audience.filters) {
          const f = audience.filters;
          // Location
          if (f.city) setCities(f.city);
          if (f.state) setStates(f.state);
          if (f.zip) setZipCodes(f.zip);
          // Personal
          if (f.gender) setGenders(f.gender);
          if (f.age) {
            setUseAgeFilter(true);
            setAgeRange([f.age.minAge || 18, f.age.maxAge || 65]);
          }
          // Profile (personal)
          if (f.profile) {
            if (f.profile.incomeRange) setIncomeRanges(f.profile.incomeRange);
            if (f.profile.homeowner) setHomeowner(f.profile.homeowner);
            if (f.profile.married) setMarried(f.profile.married);
            if (f.profile.netWorth) setNetWorth(f.profile.netWorth);
            if (f.profile.children) setChildren(f.profile.children);
          }
          // Attributes (financial)
          if (f.attributes) {
            const attr = f.attributes;
            if (attr.credit_rating) setCreditRating(attr.credit_rating);
            if (attr.credit_range_new_credit)
              setNewCreditRange(attr.credit_range_new_credit);
            if (attr.credit_card_user) setCreditCardUser(attr.credit_card_user);
            if (attr.investment) setInvestment(attr.investment);
            // Mortgage amount is stored as min/max - convert back to selected ranges
            if (attr.mortgage_amount) {
              const ma = attr.mortgage_amount;
              if (ma.min !== undefined && ma.max !== undefined) {
                // Find all options that fall within the saved min/max range
                const matchingOptions = mortgageAmountOptions
                  .filter((o) => o.min >= ma.min && o.max <= ma.max)
                  .map((o) => o.value);
                setMortgageAmount(matchingOptions);
              } else if (Array.isArray(ma)) {
                // Legacy format - array of strings
                setMortgageAmount(ma);
              }
            }
            if (attr.occupation_group) setOccupationGroup(attr.occupation_group);
            if (attr.cra_code) setCraCode(attr.cra_code);
            // Personal attributes
            if (attr.ethnic_code) setEthnicity(attr.ethnic_code);
            if (attr.language_code) setLanguage(attr.language_code);
            if (attr.education) setEducation(attr.education);
            if (attr.smoker) setSmoker(attr.smoker);
            // Home attributes
            if (attr.dwelling_type) setDwellingType(attr.dwelling_type);
            if (attr.home_year_built) {
              if (attr.home_year_built.min)
                setHomeYearBuiltMin(attr.home_year_built.min);
              if (attr.home_year_built.max)
                setHomeYearBuiltMax(attr.home_year_built.max);
            }
            if (attr.home_purchase_price) {
              if (attr.home_purchase_price.min)
                setHomePurchasePriceMin(attr.home_purchase_price.min);
              if (attr.home_purchase_price.max)
                setHomePurchasePriceMax(attr.home_purchase_price.max);
            }
            if (attr.home_purchase_year) {
              if (attr.home_purchase_year.min)
                setHomePurchaseYearMin(attr.home_purchase_year.min);
              if (attr.home_purchase_year.max)
                setHomePurchaseYearMax(attr.home_purchase_year.max);
            }
            if (attr.estimated_home_value)
              setEstimatedHomeValue(attr.estimated_home_value);
          }
          // Business profile
          if (f.businessProfile) {
            const bp = f.businessProfile;
            if (bp.industry) setIndustries(bp.industry);
            if (bp.department) setDepartments(bp.department);
            if (bp.seniority) setSeniorities(bp.seniority);
            if (bp.jobTitle) setJobTitles(bp.jobTitle);
            if (bp.companyName) setCompanyNames(bp.companyName);
            if (bp.companyDomain) setCompanyDomains(bp.companyDomain);
            if (bp.employeeCount) setEmployeeCounts(bp.employeeCount);
            if (bp.companyRevenue) setCompanyRevenues(bp.companyRevenue);
            if (bp.sic) setSicCodes(bp.sic);
            if (bp.companyNaics) setNaicsCodes(bp.companyNaics);
            if (bp.companyDescription) setCompanyDescriptions(bp.companyDescription);
          }
          // Contact filters (notNulls)
          if (f.notNulls && f.notNulls.length > 0) {
            setContactNotNulls(f.notNulls);
          }
        }

        // Segments - use segmentDetails if available, otherwise fall back to segment IDs
        if (audience.segmentDetails && audience.segmentDetails.length > 0) {
          // Use enriched segment details from backend (has id as externalId and name)
          const segmentObjects = audience.segmentDetails.map(
            (s: { externalId: number; name: string }) => ({
              id: s.externalId,
              name: s.name,
            })
          );
          setSelectedSegments(segmentObjects);
        } else if (audience.segment && audience.segment.length > 0) {
          const firstItem = audience.segment[0];
          if (typeof firstItem === "object" && firstItem.id && firstItem.name) {
            // Array of objects with id and name
            setSelectedSegments(audience.segment);
          } else {
            // Array of IDs - create placeholder names
            const segmentObjects = audience.segment.map(
              (id: number | string) => ({
                id: typeof id === "string" ? parseInt(id, 10) : id,
                name: `Segment ${id}`,
              })
            );
            setSelectedSegments(segmentObjects);
          }
        }

        // Days back
        if (audience.daysBack) {
          setDaysBack(audience.daysBack);
        }

        // Score - convert array to minimum score
        if (audience.score && audience.score.length > 0) {
          // The score array contains all scores >= minimum, so the first one is the minimum
          const scoreOrder = ["low", "medium", "high"];
          const scores = audience.score as string[];
          // Find the lowest score in the array
          let minScore = "";
          for (const s of scoreOrder) {
            if (scores.includes(s)) {
              minScore = s;
              break;
            }
          }
          if (minScore) setMinimumScore(minScore);
        }

        // Audience config (keyword search)
        if (audience.audienceConfig) {
          const ac = audience.audienceConfig;
          if (ac.type === "keyword") {
            setAudienceType("keyword");
            if (ac.segmentSearches) setSegmentSearches(ac.segmentSearches);
          }
        }

        // Load preview automatically for existing audience
        await loadPreview(1, audience.audienceId);
      } else {
        setListAudience(null);
        setHasPreview(false);
        setPreviewContacts([]);
        setIsSaved(false);
        setPreviewAudienceId(null);
        // Default audience name to list name when creating new audience
        if (listName) {
          setAudienceName(listName);
        }
      }
    } catch (err) {
      console.error("Failed to load existing audience:", err);
      setListAudience(null);
      setIsSaved(false);
    } finally {
      setLoadingAudience(false);
    }
  };

  // Load filters from a previous audience
  const loadFiltersFromPreviousAudience = (audience: any) => {
    // Clear existing preview state since we're loading new filters
    setHasPreview(false);
    setPreviewContacts([]);
    setPreviewAudienceId(null);
    setIsSaved(false);

    // Populate filters from the selected audience
    if (audience.filters) {
      const f = audience.filters;
      // Location
      if (f.city) setCities(f.city);
      else setCities([]);
      if (f.state) setStates(f.state);
      else setStates([]);
      if (f.zip) setZipCodes(f.zip);
      else setZipCodes([]);
      // Personal
      if (f.gender) setGenders(f.gender);
      else setGenders([]);
      if (f.age) {
        setUseAgeFilter(true);
        setAgeRange([f.age.minAge || 18, f.age.maxAge || 65]);
      } else {
        setUseAgeFilter(false);
        setAgeRange([18, 65]);
      }
      // Profile (personal)
      if (f.profile) {
        if (f.profile.incomeRange) setIncomeRanges(f.profile.incomeRange);
        else setIncomeRanges([]);
        if (f.profile.homeowner) setHomeowner(f.profile.homeowner);
        else setHomeowner([]);
        if (f.profile.married) setMarried(f.profile.married);
        else setMarried([]);
        if (f.profile.netWorth) setNetWorth(f.profile.netWorth);
        else setNetWorth([]);
        if (f.profile.children) setChildren(f.profile.children);
        else setChildren([]);
      } else {
        setIncomeRanges([]);
        setHomeowner([]);
        setMarried([]);
        setNetWorth([]);
        setChildren([]);
      }
      // Attributes (financial)
      if (f.attributes) {
        const attr = f.attributes;
        if (attr.credit_rating) setCreditRating(attr.credit_rating);
        else setCreditRating([]);
        if (attr.credit_range_new_credit) setNewCreditRange(attr.credit_range_new_credit);
        else setNewCreditRange([]);
        if (attr.credit_card_user) setCreditCardUser(attr.credit_card_user);
        else setCreditCardUser([]);
        if (attr.investment) setInvestment(attr.investment);
        else setInvestment([]);
        // Mortgage amount
        if (attr.mortgage_amount) {
          const ma = attr.mortgage_amount;
          if (ma.min !== undefined && ma.max !== undefined) {
            const matchingOptions = mortgageAmountOptions
              .filter((o) => o.min >= ma.min && o.max <= ma.max)
              .map((o) => o.value);
            setMortgageAmount(matchingOptions);
          } else if (Array.isArray(ma)) {
            setMortgageAmount(ma);
          }
        } else {
          setMortgageAmount([]);
        }
        if (attr.occupation_group) setOccupationGroup(attr.occupation_group);
        else setOccupationGroup([]);
        if (attr.cra_code) setCraCode(attr.cra_code);
        else setCraCode([]);
        // Personal attributes
        if (attr.ethnic_code) setEthnicity(attr.ethnic_code);
        else setEthnicity([]);
        if (attr.language_code) setLanguage(attr.language_code);
        else setLanguage([]);
        if (attr.education) setEducation(attr.education);
        else setEducation([]);
        if (attr.smoker) setSmoker(attr.smoker);
        else setSmoker([]);
        // Home attributes
        if (attr.dwelling_type) setDwellingType(attr.dwelling_type);
        else setDwellingType([]);
        if (attr.home_year_built) {
          if (attr.home_year_built.min) setHomeYearBuiltMin(attr.home_year_built.min);
          else setHomeYearBuiltMin("");
          if (attr.home_year_built.max) setHomeYearBuiltMax(attr.home_year_built.max);
          else setHomeYearBuiltMax("");
        } else {
          setHomeYearBuiltMin("");
          setHomeYearBuiltMax("");
        }
        if (attr.home_purchase_price) {
          if (attr.home_purchase_price.min) setHomePurchasePriceMin(attr.home_purchase_price.min);
          else setHomePurchasePriceMin("");
          if (attr.home_purchase_price.max) setHomePurchasePriceMax(attr.home_purchase_price.max);
          else setHomePurchasePriceMax("");
        } else {
          setHomePurchasePriceMin("");
          setHomePurchasePriceMax("");
        }
        if (attr.home_purchase_year) {
          if (attr.home_purchase_year.min) setHomePurchaseYearMin(attr.home_purchase_year.min);
          else setHomePurchaseYearMin("");
          if (attr.home_purchase_year.max) setHomePurchaseYearMax(attr.home_purchase_year.max);
          else setHomePurchaseYearMax("");
        } else {
          setHomePurchaseYearMin("");
          setHomePurchaseYearMax("");
        }
        if (attr.estimated_home_value) setEstimatedHomeValue(attr.estimated_home_value);
        else setEstimatedHomeValue([]);
      } else {
        // Reset all attribute filters
        setCreditRating([]);
        setNewCreditRange([]);
        setCreditCardUser([]);
        setInvestment([]);
        setMortgageAmount([]);
        setOccupationGroup([]);
        setCraCode([]);
        setEthnicity([]);
        setLanguage([]);
        setEducation([]);
        setSmoker([]);
        setDwellingType([]);
        setHomeYearBuiltMin("");
        setHomeYearBuiltMax("");
        setHomePurchasePriceMin("");
        setHomePurchasePriceMax("");
        setHomePurchaseYearMin("");
        setHomePurchaseYearMax("");
        setEstimatedHomeValue([]);
      }
      // Business profile
      if (f.businessProfile) {
        const bp = f.businessProfile;
        if (bp.industry) setIndustries(bp.industry);
        else setIndustries([]);
        if (bp.department) setDepartments(bp.department);
        else setDepartments([]);
        if (bp.seniority) setSeniorities(bp.seniority);
        else setSeniorities([]);
        if (bp.jobTitle) setJobTitles(bp.jobTitle);
        else setJobTitles([]);
        if (bp.companyName) setCompanyNames(bp.companyName);
        else setCompanyNames([]);
        if (bp.companyDomain) setCompanyDomains(bp.companyDomain);
        else setCompanyDomains([]);
        if (bp.employeeCount) setEmployeeCounts(bp.employeeCount);
        else setEmployeeCounts([]);
        if (bp.companyRevenue) setCompanyRevenues(bp.companyRevenue);
        else setCompanyRevenues([]);
        if (bp.sic) setSicCodes(bp.sic);
        else setSicCodes([]);
        if (bp.companyNaics) setNaicsCodes(bp.companyNaics);
        else setNaicsCodes([]);
        if (bp.companyDescription) setCompanyDescriptions(bp.companyDescription);
        else setCompanyDescriptions([]);
      } else {
        // Reset all business profile filters
        setIndustries([]);
        setDepartments([]);
        setSeniorities([]);
        setJobTitles([]);
        setCompanyNames([]);
        setCompanyDomains([]);
        setEmployeeCounts([]);
        setCompanyRevenues([]);
        setSicCodes([]);
        setNaicsCodes([]);
        setCompanyDescriptions([]);
      }
      // Contact filters (notNulls)
      if (f.notNulls && f.notNulls.length > 0) {
        setContactNotNulls(f.notNulls);
      } else {
        setContactNotNulls([]);
      }
    } else {
      // No filters - reset all to defaults
      setCities([]);
      setStates([]);
      setZipCodes([]);
      setGenders([]);
      setUseAgeFilter(false);
      setAgeRange([18, 65]);
      setIncomeRanges([]);
      setHomeowner([]);
      setMarried([]);
      setNetWorth([]);
      setChildren([]);
      setCreditRating([]);
      setNewCreditRange([]);
      setCreditCardUser([]);
      setInvestment([]);
      setMortgageAmount([]);
      setOccupationGroup([]);
      setCraCode([]);
      setEthnicity([]);
      setLanguage([]);
      setEducation([]);
      setSmoker([]);
      setDwellingType([]);
      setHomeYearBuiltMin("");
      setHomeYearBuiltMax("");
      setHomePurchasePriceMin("");
      setHomePurchasePriceMax("");
      setHomePurchaseYearMin("");
      setHomePurchaseYearMax("");
      setEstimatedHomeValue([]);
      setIndustries([]);
      setDepartments([]);
      setSeniorities([]);
      setJobTitles([]);
      setCompanyNames([]);
      setCompanyDomains([]);
      setEmployeeCounts([]);
      setCompanyRevenues([]);
      setSicCodes([]);
      setNaicsCodes([]);
      setCompanyDescriptions([]);
      setContactNotNulls([]);
    }

    // Handle segments - we only copy segment IDs, not link them
    if (audience.segment && audience.segment.length > 0) {
      const firstItem = audience.segment[0];
      if (typeof firstItem === "object" && firstItem.id && firstItem.name) {
        setSelectedSegments(audience.segment);
      } else {
        const segmentObjects = audience.segment.map((id: number | string) => ({
          id: typeof id === "string" ? parseInt(id, 10) : id,
          name: `Segment ${id}`,
        }));
        setSelectedSegments(segmentObjects);
      }
    } else {
      setSelectedSegments([]);
    }

    // Days back
    if (audience.daysBack) {
      setDaysBack(audience.daysBack);
    } else {
      setDaysBack(7);
    }

    // Score
    if (audience.score && audience.score.length > 0) {
      const scoreOrder = ["low", "medium", "high"];
      const scores = audience.score as string[];
      let minScore = "";
      for (const s of scoreOrder) {
        if (scores.includes(s)) {
          minScore = s;
          break;
        }
      }
      if (minScore) setMinimumScore(minScore);
      else setMinimumScore("");
    } else {
      setMinimumScore("");
    }

    // Audience config (keyword search)
    if (audience.audienceConfig) {
      const ac = audience.audienceConfig;
      if (ac.type === "keyword") {
        setAudienceType("keyword");
        if (ac.segmentSearches) setSegmentSearches(ac.segmentSearches);
        else setSegmentSearches([]);
      } else {
        setAudienceType("premade");
        setSegmentSearches([]);
      }
    } else {
      setAudienceType("premade");
      setSegmentSearches([]);
    }

    setStatusMessage({
      type: "success",
      message: `Filters loaded from "${audience.audienceName}"`,
    });
  };

  // Track polling attempts
  const [pollAttempts, setPollAttempts] = useState(0);

  const pollAudienceStatus = async (
    audienceIdToPoll: string,
    attempt: number = 0
  ) => {
    setPollingForStatus(true);
    setPollAttempts(attempt);

    try {
      const token = await getToken();

      // Use the preview status endpoint to check if audience is ready (doesn't create new audiences)
      const statusResponse = await axios.get(
        `${hostname}/lists/${listId}/audience/preview/status`,
        {
          params: { companyId, audienceId: audienceIdToPoll },
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const { status, totalRecords } = statusResponse.data;
      const statusLower = status?.toLowerCase();
      const isStillBuilding = ["building", "processing", "hydrating", "pending", "queued", "in_queue"].includes(statusLower);

      if (isStillBuilding) {
        // Still building - poll again
        if (attempt < MAX_POLL_ATTEMPTS) {
          setTimeout(
            () => pollAudienceStatus(audienceIdToPoll, attempt + 1),
            3000
          );
        } else {
          // Give up after max attempts
          setPollingForStatus(false);
          setAudienceStatus("ready");
          setStatusMessage({
            type: "warning",
            message:
              "Audience is taking longer than expected. Click 'Refresh Preview' to check again.",
          });
        }
      } else {
        // Audience is ready - now fetch the preview data using the existing audience ID
        // The preview endpoint will use the existing audience since we pass previewAudienceId
        const previewResponse = await axios.post(
          `${hostname}/lists/${listId}/audience/preview`,
          {
            companyId,
            audienceName: audienceName || `Audience for List ${listId}`,
            filters: buildFilters(),
            segment: selectedSegments.map((s) => s.id),
            daysBack: daysBack >= 2 ? daysBack : undefined,
            score: buildScoreArray(),
            audience: buildAudienceConfig(),
            page: 1,
            pageSize,
            previewAudienceId: audienceIdToPoll,
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        setPollingForStatus(false);
        setAudienceStatus("ready");

        const { preview, pagination } = previewResponse.data;
        setPreviewContacts(preview || []);
        setTotalRecords(pagination?.totalRecords || totalRecords || 0);
        setHasPreview(true);
      }
    } catch (err: any) {
      console.error("Failed to poll audience status:", err);

      // Check if this is a validation error
      const errorMessage = parseError(err, "");
      const isValidationError =
        errorMessage.includes("At least one filter") ||
        errorMessage.includes("required") ||
        (err.response && err.response.status === 400);

      if (isValidationError) {
        // Stop polling and show the error
        setPollingForStatus(false);
        setAudienceStatus("ready");
        setStatusMessage({
          type: "error",
          message: errorMessage || "Invalid audience configuration.",
        });
        return;
      }

      // Keep polling on transient errors (might be temporary)
      if (attempt < MAX_POLL_ATTEMPTS) {
        setTimeout(
          () => pollAudienceStatus(audienceIdToPoll, attempt + 1),
          3000
        );
      } else {
        setPollingForStatus(false);
        setStatusMessage({
          type: "warning",
          message:
            "Could not load preview. Click 'Refresh Preview' to try again.",
        });
      }
    }
  };

  const loadPreview = async (
    page: number,
    existingAudienceId?: string,
    forceNew: boolean = false,
    isRefresh: boolean = false
  ) => {
    const setLoading = (loading: boolean) => {
      if (isRefresh) {
        setRefreshPreviewLoading(loading);
      } else {
        setPreviewLoading(loading);
      }
    };

    setLoading(true);
    setCurrentPage(page);

    try {
      const token = await getToken();
      const response = await axios.post(
        `${hostname}/lists/${listId}/audience/preview`,
        {
          companyId,
          audienceName: audienceName || `Audience for List ${listId}`,
          filters: buildFilters(),
          segment: selectedSegments.map((s) => s.id),
          daysBack: daysBack >= 2 ? daysBack : undefined,
          score: buildScoreArray(),
          audience: buildAudienceConfig(),
          page,
          pageSize,
          // Pass existing preview audience ID for reuse or deletion
          previewAudienceId: previewAudienceId,
          // Force creating a new audience with updated filters
          forceNew: forceNew,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const {
        preview,
        pagination,
        audience,
        status,
        previewAudienceId: newPreviewId,
        isSaved: savedStatus,
      } = response.data;

      // Track the preview audience ID for subsequent previews
      if (newPreviewId) {
        setPreviewAudienceId(newPreviewId);
      }
      setIsSaved(savedStatus || false);

      // Check if audience is still building (status is building/processing/hydrating OR empty results on new audience)
      const statusLower = status?.toLowerCase();
      const isStillBuildingStatus = ["building", "processing", "hydrating", "pending", "queued", "in_queue"].includes(statusLower);
      // Empty results only mean "still hydrating" when there's an audience to poll.
      // The synchronous /audiences/preview path returns no previewAudienceId, so an
      // empty sample there is a final "no matches" answer, not a building state.
      const isBuilding =
        isStillBuildingStatus ||
        (preview &&
          preview.length === 0 &&
          pagination?.totalRecords === 0 &&
          !isSaved &&
          !!newPreviewId);

      if (isBuilding) {
        setAudienceStatus("building");
        setHasPreview(true);
        setPreviewContacts([]);
        setTotalRecords(0);

        // Get the audience ID to poll - either from the response or the existing one
        const audienceIdToPoll =
          newPreviewId || existingAudienceId || audience?.audienceId;

        if (audienceIdToPoll) {
          setLoading(false);
          setTimeout(() => pollAudienceStatus(audienceIdToPoll, 0), 3000);
        } else {
          setLoading(false);
          setStatusMessage({
            type: "error",
            message: "Failed to create audience for polling.",
          });
        }
        return;
      }

      setAudienceStatus(status || "ready");
      setPreviewContacts(preview || []);
      setTotalRecords(pagination?.totalRecords || 0);
      setHasPreview(true);

      if (audience) {
        setListAudience(audience);
      }
    } catch (err) {
      setStatusMessage({
        type: "error",
        message: parseError(err) || "Failed to load preview.",
      });
    } finally {
      setLoading(false);
    }
  };

  const loadAttributes = async () => {
    setLoadingAttributes(true);
    try {
      const token = await getToken();

      try {
        const industriesResponse = await axios.get(
          `${hostname}/integrations/audience/attributes/industries`,
          {
            params: { companyId },
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        const industriesData =
          industriesResponse.data?.attributes?.industries?.data || [];
        if (industriesData.length > 0) {
          setIndustryOptions(industriesData);
        }
      } catch (err) {
        console.error("Failed to load industries:", err);
      }
    } finally {
      setLoadingAttributes(false);
    }
  };

  // Search segments with debounce
  const searchSegments = useCallback(
    async (query: string) => {
      if (!query || query.length < 2) {
        setSegmentOptions([]);
        return;
      }

      setLoadingSegments(true);
      try {
        const token = await getToken();
        const response = await axios.get(
          `${hostname}/integrations/audience/segments/search`,
          {
            params: { companyId, q: query, limit: 50 },
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setSegmentOptions(response.data.data || []);
      } catch (err) {
        console.error("Failed to search segments:", err);
        setSegmentOptions([]);
      } finally {
        setLoadingSegments(false);
      }
    },
    [companyId, getToken]
  );

  const debouncedSearchSegments = useMemo(
    () => debounce(searchSegments, 300),
    [searchSegments]
  );

  // Filter count calculations for accordion headers
  const intentFilterCount = useMemo(() => {
    let count = 0;
    if (audienceType === "premade" && selectedSegments.length > 0) count++;
    if (audienceType === "keyword" && segmentSearches.length > 0) count++;
    if (minimumScore) count++;
    return count;
  }, [audienceType, selectedSegments, segmentSearches, minimumScore]);

  const businessFilterCount = useMemo(() => {
    let count = 0;
    if (companyDescriptions.length > 0) count++;
    if (industries.length > 0) count++;
    if (departments.length > 0) count++;
    if (seniorities.length > 0) count++;
    if (jobTitles.length > 0) count++;
    if (companyNames.length > 0) count++;
    if (companyDomains.length > 0) count++;
    if (employeeCounts.length > 0) count++;
    if (companyRevenues.length > 0) count++;
    if (sicCodes.length > 0) count++;
    if (naicsCodes.length > 0) count++;
    return count;
  }, [companyDescriptions, industries, departments, seniorities, jobTitles, companyNames, companyDomains, employeeCounts, companyRevenues, sicCodes, naicsCodes]);

  const financialFilterCount = useMemo(() => {
    let count = 0;
    if (incomeRanges.length > 0) count++;
    if (netWorth.length > 0) count++;
    if (creditRating.length > 0) count++;
    if (newCreditRange.length > 0) count++;
    if (creditCardUser.length > 0) count++;
    if (investment.length > 0) count++;
    if (mortgageAmount.length > 0) count++;
    if (occupationGroup.length > 0) count++;
    if (craCode.length > 0) count++;
    return count;
  }, [incomeRanges, netWorth, creditRating, newCreditRange, creditCardUser, investment, mortgageAmount, occupationGroup, craCode]);

  const personalFilterCount = useMemo(() => {
    let count = 0;
    if (genders.length > 0) count++;
    if (useAgeFilter) count++;
    if (ethnicity.length > 0) count++;
    if (language.length > 0) count++;
    if (education.length > 0) count++;
    if (smoker.length > 0) count++;
    return count;
  }, [genders, useAgeFilter, ethnicity, language, education, smoker]);

  const housingFilterCount = useMemo(() => {
    let count = 0;
    if (homeowner.length > 0) count++;
    if (dwellingType.length > 0) count++;
    if (homeYearBuiltMin || homeYearBuiltMax) count++;
    if (homePurchasePriceMin || homePurchasePriceMax) count++;
    if (homePurchaseYearMin || homePurchaseYearMax) count++;
    if (estimatedHomeValue.length > 0) count++;
    return count;
  }, [homeowner, dwellingType, homeYearBuiltMin, homeYearBuiltMax, homePurchasePriceMin, homePurchasePriceMax, homePurchaseYearMin, homePurchaseYearMax, estimatedHomeValue]);

  const familyFilterCount = useMemo(() => {
    let count = 0;
    if (married.length > 0) count++;
    if (children.length > 0) count++;
    return count;
  }, [married, children]);

  const locationFilterCount = useMemo(() => {
    let count = 0;
    if (cities.length > 0) count++;
    if (states.length > 0) count++;
    if (zipCodes.length > 0) count++;
    return count;
  }, [cities, states, zipCodes]);

  const contactFilterCount = useMemo(() => {
    return contactNotNulls.length;
  }, [contactNotNulls]);

  useEffect(() => {
    return () => {
      debouncedSearchSegments.clear();
    };
  }, [debouncedSearchSegments]);

  const buildFilters = () => {
    const filters: Record<string, any> = {};

    if (useAgeFilter) {
      filters.age = {
        minAge: ageRange[0],
        maxAge: ageRange[1],
      };
    }

    if (cities.length > 0) filters.city = cities;
    if (states.length > 0) filters.state = states;
    if (zipCodes.length > 0) filters.zip = zipCodes;
    if (genders.length > 0) filters.gender = genders;

    const profile: Record<string, any> = {};
    if (incomeRanges.length > 0) profile.incomeRange = incomeRanges;
    if (homeowner.length > 0) profile.homeowner = homeowner;
    if (married.length > 0) profile.married = married;
    if (netWorth.length > 0) profile.netWorth = netWorth;
    if (children.length > 0) profile.children = children;
    if (Object.keys(profile).length > 0) filters.profile = profile;

    // Financial attributes (filters.attributes.*)
    const attributes: Record<string, any> = {};
    if (creditRating.length > 0) attributes.credit_rating = creditRating;
    if (newCreditRange.length > 0)
      attributes.credit_range_new_credit = newCreditRange;
    if (creditCardUser.length > 0) attributes.credit_card_user = creditCardUser;
    if (investment.length > 0) attributes.investment = investment;
    // Mortgage amount uses min/max format - calculate from selected ranges
    if (mortgageAmount.length > 0) {
      const selectedOptions = mortgageAmount
        .map((v) => mortgageAmountOptions.find((o) => o.value === v))
        .filter((o) => o !== undefined);
      if (selectedOptions.length > 0) {
        const minVal = Math.min(...selectedOptions.map((o) => o!.min));
        const maxVal = Math.max(...selectedOptions.map((o) => o!.max));
        attributes.mortgage_amount = { min: minVal, max: maxVal };
      }
    }
    if (occupationGroup.length > 0)
      attributes.occupation_group = occupationGroup;
    if (craCode.length > 0) attributes.cra_code = craCode;
    // Personal attributes
    if (ethnicity.length > 0) attributes.ethnic_code = ethnicity;
    if (language.length > 0) attributes.language_code = language;
    if (education.length > 0) attributes.education = education;
    if (smoker.length > 0) attributes.smoker = smoker;
    // Home attributes
    if (dwellingType.length > 0) attributes.dwelling_type = dwellingType;
    if (homeYearBuiltMin || homeYearBuiltMax) {
      attributes.home_year_built = {
        ...(homeYearBuiltMin && { min: homeYearBuiltMin }),
        ...(homeYearBuiltMax && { max: homeYearBuiltMax }),
      };
    }
    if (homePurchasePriceMin || homePurchasePriceMax) {
      attributes.home_purchase_price = {
        ...(homePurchasePriceMin && { min: homePurchasePriceMin }),
        ...(homePurchasePriceMax && { max: homePurchasePriceMax }),
      };
    }
    if (homePurchaseYearMin || homePurchaseYearMax) {
      attributes.home_purchase_year = {
        ...(homePurchaseYearMin && { min: homePurchaseYearMin }),
        ...(homePurchaseYearMax && { max: homePurchaseYearMax }),
      };
    }
    if (estimatedHomeValue.length > 0)
      attributes.estimated_home_value = estimatedHomeValue;
    if (Object.keys(attributes).length > 0) filters.attributes = attributes;

    const businessProfile: Record<string, any> = {};
    if (industries.length > 0) businessProfile.industry = industries;
    if (departments.length > 0) businessProfile.department = departments;
    if (seniorities.length > 0) businessProfile.seniority = seniorities;
    if (jobTitles.length > 0) businessProfile.jobTitle = jobTitles;
    if (companyNames.length > 0) businessProfile.companyName = companyNames;
    if (companyDomains.length > 0)
      businessProfile.companyDomain = companyDomains;
    if (employeeCounts.length > 0)
      businessProfile.employeeCount = employeeCounts;
    if (companyRevenues.length > 0)
      businessProfile.companyRevenue = companyRevenues;
    if (sicCodes.length > 0) businessProfile.sic = sicCodes;
    if (naicsCodes.length > 0) businessProfile.companyNaics = naicsCodes;
    if (companyDescriptions.length > 0)
      businessProfile.companyDescription = companyDescriptions;
    if (Object.keys(businessProfile).length > 0)
      filters.businessProfile = businessProfile;

    // Contact filters (notNulls)
    if (contactNotNulls.length > 0) filters.notNulls = contactNotNulls;

    return filters;
  };

  const buildScoreArray = () => {
    if (!minimumScore) return undefined;
    const scoreHierarchy = ["low", "medium", "high"];
    const minIndex = scoreHierarchy.indexOf(minimumScore);
    return scoreHierarchy.slice(minIndex);
  };

  const buildAudienceConfig = () => {
    if (audienceType === "keyword" && segmentSearches.length > 0) {
      const config: {
        type: string;
        b2b: boolean;
        segmentSearches: string[];
        days_back?: number;
      } = {
        type: "keyword",
        b2b: false,
        segmentSearches: segmentSearches,
      };
      // Include days_back in the audience config for keyword-based searches
      if (daysBack >= 2) {
        config.days_back = daysBack;
      }
      return config;
    }
    return undefined;
  };

  const hasFilters = () => {
    return (
      cities.length > 0 ||
      states.length > 0 ||
      zipCodes.length > 0 ||
      genders.length > 0 ||
      useAgeFilter ||
      industries.length > 0 ||
      departments.length > 0 ||
      seniorities.length > 0 ||
      jobTitles.length > 0 ||
      companyNames.length > 0 ||
      companyDomains.length > 0 ||
      employeeCounts.length > 0 ||
      companyRevenues.length > 0 ||
      sicCodes.length > 0 ||
      naicsCodes.length > 0 ||
      companyDescriptions.length > 0 ||
      incomeRanges.length > 0 ||
      homeowner.length > 0 ||
      married.length > 0 ||
      netWorth.length > 0 ||
      children.length > 0 ||
      creditRating.length > 0 ||
      newCreditRange.length > 0 ||
      creditCardUser.length > 0 ||
      investment.length > 0 ||
      mortgageAmount.length > 0 ||
      occupationGroup.length > 0 ||
      craCode.length > 0 ||
      ethnicity.length > 0 ||
      language.length > 0 ||
      education.length > 0 ||
      smoker.length > 0 ||
      dwellingType.length > 0 ||
      homeYearBuiltMin !== "" ||
      homeYearBuiltMax !== "" ||
      homePurchasePriceMin !== "" ||
      homePurchasePriceMax !== "" ||
      homePurchaseYearMin !== "" ||
      homePurchaseYearMax !== "" ||
      estimatedHomeValue.length > 0 ||
      contactNotNulls.length > 0 ||
      selectedSegments.length > 0 ||
      minimumScore !== "" ||
      (audienceType === "keyword" && segmentSearches.length > 0)
    );
  };

  const handlePreview = async () => {
    if (!audienceName.trim()) {
      setStatusMessage({
        type: "warning",
        message: "Please enter an audience name.",
      });
      return;
    }

    if (!hasFilters()) {
      setStatusMessage({
        type: "warning",
        message: "Please add at least one filter.",
      });
      return;
    }

    setStatusMessage(null);
    // Always force a new preview when user clicks the button
    // This ensures updated filters are applied
    await loadPreview(1, undefined, true);
  };

  const handleRefreshPreview = async () => {
    if (!previewAudienceId && !listAudience?.audienceId) {
      setStatusMessage({
        type: "warning",
        message: "No audience to refresh.",
      });
      return;
    }

    setStatusMessage(null);
    // Refresh without forceNew - just reload data for existing audience
    await loadPreview(1, undefined, false, true);
  };

  const handleSave = async () => {
    if (!audienceName.trim()) {
      setStatusMessage({
        type: "warning",
        message: "Please enter an audience name.",
      });
      return;
    }

    if (!hasFilters()) {
      setStatusMessage({
        type: "warning",
        message: "Please add at least one filter.",
      });
      return;
    }

    setSaveLoading(true);
    setStatusMessage(null);

    try {
      const token = await getToken();
      const response = await axios.post(
        `${hostname}/lists/${listId}/audience/save`,
        {
          companyId,
          audienceName,
          filters: buildFilters(),
          segment: selectedSegments.map((s) => s.id),
          segmentObjects: selectedSegments,
          daysBack: daysBack >= 2 ? daysBack : undefined,
          score: buildScoreArray(),
          audience: buildAudienceConfig(),
          previewAudienceId: previewAudienceId,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setListAudience(response.data.audience);
      setIsSaved(true);
      setPreviewAudienceId(null);
      setStatusMessage({
        type: "success",
        message: "Audience saved successfully.",
      });
    } catch (err) {
      setStatusMessage({
        type: "error",
        message: parseError(err) || "Failed to save audience.",
      });
    } finally {
      setSaveLoading(false);
    }
  };

  const handleRefreshSettingsSave = (audience: any) => {
    setListAudience(audience);
    setRefreshInterval(audience.refreshInterval || "none");
    setMaxRecordsPerRefresh(audience.maxRecordsPerRefresh);
    setEmailNotificationEnabled(audience.emailNotificationEnabled || false);
    setEmailNotificationRecipients(audience.emailNotificationRecipients || []);
    setEmailNotificationFormat(audience.emailNotificationFormat || "csv");
    setStatusMessage({
      type: "success",
      message: "Refresh settings saved.",
    });
  };

  const handleGenerateKeywords = async () => {
    if (!keywordGeneratorInput.trim()) {
      return;
    }

    setGeneratingKeywords(true);

    try {
      const token = await getToken();
      const response = await axios.post(
        `${hostname}/integrations/audience/generate-keywords`,
        {
          companyId,
          description: keywordGeneratorInput.trim(),
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const generatedKeywords = response.data.keywords || [];

      // Add generated keywords to existing companyDescriptions
      setCompanyDescriptions((prev) => {
        const newKeywords = generatedKeywords.filter(
          (kw: string) => !prev.includes(kw)
        );
        return [...prev, ...newKeywords];
      });

      // Close the generator dialog and reset input
      setShowKeywordGenerator(false);
      setKeywordGeneratorInput("");
    } catch (err) {
      console.error("Failed to generate keywords:", err);
      eventListener({
        type: "SET_ERROR",
        message: parseError(err, "Failed to generate business keywords"),
      });
    } finally {
      setGeneratingKeywords(false);
    }
  };

  const handleGenerateIntentKeywords = async () => {
    if (!intentKeywordGeneratorInput.trim()) {
      return;
    }

    setGeneratingIntentKeywords(true);

    try {
      const token = await getToken();
      const response = await axios.post(
        `${hostname}/integrations/audience/generate-intent-keywords`,
        {
          companyId,
          description: intentKeywordGeneratorInput.trim(),
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const generatedKeywords = response.data.keywords || [];

      // Add generated keywords to existing segmentSearches
      setSegmentSearches((prev) => {
        const newKeywords = generatedKeywords.filter(
          (kw: string) => !prev.includes(kw)
        );
        return [...prev, ...newKeywords];
      });

      // Close the generator dialog and reset input
      setShowIntentKeywordGenerator(false);
      setIntentKeywordGeneratorInput("");
    } catch (err) {
      console.error("Failed to generate intent keywords:", err);
      eventListener({
        type: "SET_ERROR",
        message: parseError(err, "Failed to generate intent keywords"),
      });
    } finally {
      setGeneratingIntentKeywords(false);
    }
  };

  const handleImport = async () => {
    if (!isSaved) {
      setStatusMessage({
        type: "warning",
        message: "Please save the audience before importing.",
      });
      return;
    }

    setImportLoading(true);
    setStatusMessage(null);

    try {
      const token = await getToken();

      // Determine the count to import based on importSize
      const count = importSize === "all" ? undefined : parseInt(importSize, 10);

      const response = await axios.post(
        `${hostname}/lists/${listId}/audience/import-selected`,
        {
          companyId,
          importAll: importSize === "all",
          count,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.status === "background") {
        eventListener({
          type: "SET_SUCCESS",
          message:
            response.data.message ||
            "Audience import started. This may take a few minutes.",
        });

        if (onBackgroundTaskCreated) {
          onBackgroundTaskCreated();
        }
      } else {
        eventListener({
          type: "SET_SUCCESS",
          message: `Successfully imported audience`,
        });
      }

      onClose();
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      setStatusMessage({
        type: "error",
        message: parseError(err) || "Failed to start import.",
      });
    } finally {
      setImportLoading(false);
    }
  };

  const handleExport = async () => {
    if (!hasFilters()) {
      setStatusMessage({
        type: "warning",
        message: "Please add at least one filter.",
      });
      return;
    }

    setExportLoading(true);
    setStatusMessage(null);

    try {
      const token = await getToken();
      const response = await axios.post(
        `${hostname}/lists/${listId}/audience/export`,
        {
          companyId,
          audienceName: audienceName || "Audience Export",
          filters: buildFilters(),
          segment: selectedSegments.map((s) => s.id),
          daysBack: daysBack >= 2 ? daysBack : undefined,
          score: buildScoreArray(),
          audience: buildAudienceConfig(),
          previewAudienceId: previewAudienceId,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob",
        }
      );

      // 202 Accepted -> backend returned JSON (inside a blob) describing the
      // background task. Parse it and show the "we'll email you" toast.
      if (response.status === 202) {
        const text = await (response.data as Blob).text();
        let message = "Export is processing in the background. You will receive an email when it is ready.";
        try {
          const parsed = JSON.parse(text);
          if (parsed?.message) message = parsed.message;
        } catch {
          // fall back to default message
        }
        eventListener({ type: "SET_SUCCESS", message });
        if (onBackgroundTaskCreated) {
          onBackgroundTaskCreated();
        }
        return;
      }

      // 200 OK -> CSV blob. Trigger browser download.
      const blob = new Blob([response.data as BlobPart], { type: "text/csv" });
      const disposition = response.headers["content-disposition"] as string | undefined;
      let filename = `${audienceName || "audience"}.csv`;
      if (disposition) {
        const match = /filename="?([^";]+)"?/.exec(disposition);
        if (match) filename = match[1];
      }
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      eventListener({
        type: "SET_SUCCESS",
        message: "Audience exported successfully.",
      });
    } catch (err: any) {
      // Error bodies also come back as blobs because of responseType
      let message = "Failed to export audience.";
      try {
        if (err?.response?.data instanceof Blob) {
          const text = await err.response.data.text();
          const parsed = JSON.parse(text);
          if (parsed?.error) message = parsed.error;
        } else {
          message = parseError(err) || message;
        }
      } catch {
        message = parseError(err) || message;
      }
      setStatusMessage({ type: "error", message });
    } finally {
      setExportLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!listAudience) return;

    openDialog(
      "Delete Audience",
      "Are you sure you want to delete this audience? This cannot be undone.",
      "Delete",
      async () => {
        setDeleteLoading(true);
        setStatusMessage(null);

        try {
          const token = await getToken();

          await axios.delete(
            `${hostname}/lists/${listId}/audience/audience`,
            {
              params: { companyId },
              headers: { Authorization: `Bearer ${token}` },
            }
          );

          setListAudience(null);
          setHasPreview(false);
          setPreviewContacts([]);
          setIsSaved(false);
          setPreviewAudienceId(null);
          setTotalRecords(0);
          setAudienceStatus("");
          // Keep audienceName and filters so user can re-save with same settings

          setStatusMessage({
            type: "success",
            message: "Audience deleted successfully.",
          });
        } catch (err) {
          setStatusMessage({
            type: "error",
            message: parseError(err) || "Failed to delete audience.",
          });
        } finally {
          setDeleteLoading(false);
        }
      }
    );
  };

  const resetFilters = () => {
    setCities([]);
    setStates([]);
    setZipCodes([]);
    setIndustries([]);
    setDepartments([]);
    setSeniorities([]);
    setGenders([]);
    setAgeRange([18, 65]);
    setUseAgeFilter(false);
    setDaysBack(7);
    setMinimumScore("");
    setAudienceType("premade");
    setSegmentSearches([]);
    setSelectedSegments([]);
    setIncomeRanges([]);
    setHomeowner([]);
    setMarried([]);
    setNetWorth([]);
    setChildren([]);
    setJobTitles([]);
    setCompanyNames([]);
    setCompanyDomains([]);
    setEmployeeCounts([]);
    setCompanyRevenues([]);
    setSicCodes([]);
    setNaicsCodes([]);
    setContactNotNulls([]);
  };

  const handleSelectAll = () => {
    if (selectedIds.size === previewContacts.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(previewContacts.map((_, idx) => idx)));
    }
  };

  const handleToggleSelect = (idx: number) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(idx)) {
      newSelected.delete(idx);
    } else {
      newSelected.add(idx);
    }
    setSelectedIds(newSelected);
  };

  const handlePageChange = (newPage: number) => {
    const totalPages = Math.ceil(totalRecords / pageSize);
    if (newPage >= 1 && newPage <= totalPages) {
      loadPreview(newPage);
    }
  };

  const totalPages = Math.ceil(totalRecords / pageSize);

  return (
    <Dialog open={open} onClose={onClose} fullScreen>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <AdsClick color="primary" />
          <Typography variant="h6">Audience Builder</Typography>
          {listAudience && (
            <Chip
              label={listAudience.audienceName}
              size="small"
              color="primary"
              sx={{ ml: 1 }}
            />
          )}
          <Tooltip
            title={
              <Box sx={{ p: 1, maxWidth: 360, maxHeight: 400, overflow: "auto" }}>
                <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1 }}>
                  Fields Guide
                </Typography>
                <Box sx={{ fontSize: "0.7rem", "& > div": { mb: 1.5, pb: 1, borderBottom: "1px solid", borderColor: "divider" } }}>
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: "bold", display: "block" }}>B2B Outbound Call</Typography>
                    <Typography variant="caption" color="text.secondary">Fields: SKIPTRACE_B2B_WIRELESS, SKIPTRACE_B2B_LANDLINE</Typography>
                    <Typography variant="caption" color="text.secondary" display="block">Tip: Use MATCH_BY: COMPANY_ADDRESS</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: "bold", display: "block" }}>B2B Cold Email</Typography>
                    <Typography variant="caption" color="text.secondary">Fields: BUSINESS_EMAIL</Typography>
                    <Typography variant="caption" color="text.secondary" display="block">Tip: VALIDATION_STATUS = Valid, LAST_SEEN within 30 days</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: "bold", display: "block" }}>B2C Outbound Call</Typography>
                    <Typography variant="caption" color="text.secondary">Fields: SKIPTRACE_WIRELESS_NUMBERS, SKIPTRACE_LANDLINE_NUMBERS</Typography>
                    <Typography variant="caption" color="text.secondary" display="block">Tip: DNC = N, MATCH_BY: NAME,ADDRESS</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: "bold", display: "block" }}>B2C Outbound Email</Typography>
                    <Typography variant="caption" color="text.secondary">Fields: PERSONAL_EMAIL</Typography>
                    <Typography variant="caption" color="text.secondary" display="block">Tip: VALIDATION_STATUS = Valid, LAST_SEEN within 30 days</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: "bold", display: "block" }}>Direct Mail</Typography>
                    <Typography variant="caption" color="text.secondary">Fields: SKIPTRACE_ADDRESS</Typography>
                    <Typography variant="caption" color="text.secondary" display="block">Tip: MATCH_BY: NAME,ADDRESS</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: "bold", display: "block" }}>Facebook/Meta Ads</Typography>
                    <Typography variant="caption" color="text.secondary">Fields: SHA256 emails, phones, name, gender, age, state</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: "bold", display: "block" }}>Google/YouTube Ads</Typography>
                    <Typography variant="caption" color="text.secondary">Fields: SHA256_PERSONAL_EMAIL</Typography>
                  </Box>
                  <Box sx={{ borderBottom: "none !important", mb: 0, pb: 0 }}>
                    <Typography variant="caption" sx={{ fontWeight: "bold", display: "block" }}>Programmatic Ads</Typography>
                    <Typography variant="caption" color="text.secondary">Fields: SHA256_PERSONAL_EMAIL</Typography>
                  </Box>
                </Box>
              </Box>
            }
            placement="bottom-start"
            componentsProps={{
              tooltip: {
                sx: {
                  bgcolor: "background.paper",
                  color: "text.primary",
                  boxShadow: 3,
                  "& .MuiTooltip-arrow": {
                    color: "background.paper",
                  },
                },
              },
            }}
          >
            <IconButton size="small" sx={{ ml: 0.5 }}>
              <HelpOutline fontSize="small" color="action" />
            </IconButton>
          </Tooltip>
        </Box>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {loadingAudience ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              gap: 2,
            }}
          >
            <CircularProgress color="inherit" />
            <Typography>Loading audience...</Typography>
          </Box>
        ) : (
        <Box sx={{ display: "flex", height: "100%" }}>
          {/* Left Panel - Filters */}
          <Box
            sx={{
              width: 440,
              minWidth: 440,
              borderRight: 1,
              borderColor: "divider",
              overflowY: "auto",
              p: 2,
            }}
          >
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              {listAudience ? "Audience Filters" : "Create Audience"}
            </Typography>

            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                {listAudience
                  ? "This list has an audience configured. Preview the data and import when ready."
                  : "Build an audience using filters below. Preview to see matching contacts, then import."}
              </Typography>
            </Alert>

            <Typography
              variant="body2"
              color="primary"
              sx={{
                mb: 2,
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                "&:hover": { textDecoration: "underline" },
              }}
              onClick={() => setPreviousAudiencesDialogOpen(true)}
            >
              <History fontSize="small" />
              Load filters from a previous audience
            </Typography>

            {statusMessage && (
              <Alert severity={statusMessage.type} sx={{ mb: 2 }}>
                {statusMessage.message}
              </Alert>
            )}

            {/* Audience Name */}
            <TextField
              fullWidth
              size="small"
              label="Audience Name"
              value={audienceName}
              onChange={(e) => setAudienceName(e.target.value)}
              required
              placeholder="e.g., Marketing Directors in SF Bay Area"
              helperText="Give your audience a descriptive name"
              sx={{ mb: 2 }}
            />

            {/* Intent Section */}
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
                  <Typography fontWeight="medium">Intent</Typography>
                  {intentFilterCount > 0 && (
                    <Chip label={`${intentFilterCount} applied`} size="small" />
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={2}>
                  <Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      gutterBottom
                    >
                      Audience Method
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                      Select the method you want to use to create your audience.
                    </Typography>
                    <ToggleButtonGroup
                      value={audienceType}
                      exclusive
                      onChange={(_e, newValue) => {
                        if (newValue !== null) setAudienceType(newValue);
                      }}
                      fullWidth
                      size="small"
                    >
                      <ToggleButton value="premade">
                        Premade
                        <Tooltip title="Premade audiences are pre-built segments with highly accurate targeting based on verified behavioral data and purchase signals.">
                          <HelpOutline sx={{ fontSize: 16, ml: 0.5, color: "text.secondary" }} />
                        </Tooltip>
                      </ToggleButton>
                      <ToggleButton value="keyword">
                        Keyword
                        <Tooltip title="Keyword matching provides broader audience reach by matching against search terms and content engagement, but may be less precise than premade segments.">
                          <HelpOutline sx={{ fontSize: 16, ml: 0.5, color: "text.secondary" }} />
                        </Tooltip>
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </Box>

                  {audienceType === "premade" && (
                    <Box>
                      <TextField
                        fullWidth
                        size="small"
                        label="Audience Segments"
                        placeholder="Click to search and select segments..."
                        value=""
                        onClick={() => setSegmentSearchDialogOpen(true)}
                        InputProps={{
                          readOnly: true,
                          startAdornment: (
                            <Search sx={{ color: "action.active", mr: 1 }} />
                          ),
                          sx: { cursor: "pointer" },
                        }}
                        sx={{ cursor: "pointer" }}
                        helperText="Click to open segment search with categories"
                      />
                      {selectedSegments.length > 0 && (
                        <Stack
                          direction="row"
                          spacing={0.5}
                          flexWrap="wrap"
                          useFlexGap
                          sx={{ mt: 1 }}
                        >
                          {selectedSegments.map((segment) => (
                            <Chip
                              key={segment.id}
                              label={segment.name}
                              size="small"
                              onDelete={() =>
                                setSelectedSegments(
                                  selectedSegments.filter((s) => s.id !== segment.id)
                                )
                              }
                              sx={{ mb: 0.5 }}
                            />
                          ))}
                        </Stack>
                      )}
                    </Box>
                  )}

                  {audienceType === "keyword" && (
                    <Box>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          mb: 1,
                        }}
                      >
                        <Typography variant="subtitle2">
                          Intent Keywords
                        </Typography>
                        <LoadingButton
                          size="small"
                          variant="outlined"
                          onClick={() => setShowIntentKeywordGenerator(true)}
                          startIcon={<AutoAwesome />}
                        >
                          AI Generator
                        </LoadingButton>
                      </Box>
                      <Autocomplete
                        multiple
                        freeSolo
                        options={[]}
                        value={segmentSearches}
                        onChange={(_e, newValue) =>
                          setSegmentSearches(newValue)
                        }
                        size="small"
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => (
                            <Chip
                              label={option}
                              size="small"
                              {...getTagProps({ index })}
                              key={option}
                            />
                          ))
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Keywords"
                            placeholder="Add keywords or use AI Generator..."
                            helperText="Enter keywords to search for intent signals (e.g., 'CRM software')"
                          />
                        )}
                      />
                    </Box>
                  )}

                  <Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      gutterBottom
                    >
                      Minimum Score
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mb: 1 }}
                    >
                      Intent scores are based on behavior deviation, distinguishing
                      casual browsers from serious buyers. Higher scores mean smaller,
                      more precise audiences ready to convert.
                    </Typography>
                    <ToggleButtonGroup
                      value={minimumScore}
                      exclusive
                      onChange={(_e, newValue) => {
                        if (newValue !== null) setMinimumScore(newValue);
                      }}
                      fullWidth
                      size="small"
                    >
                      <Tooltip title="Score 65-70: Early research phase. Largest audience, ideal for brand awareness campaigns." placement="top">
                        <ToggleButton value="low">Low</ToggleButton>
                      </Tooltip>
                      <Tooltip title="Score 75: Mid-research phase, exploring options. Balanced audience size, ideal for paid ads." placement="top">
                        <ToggleButton value="medium">Medium</ToggleButton>
                      </Tooltip>
                      <Tooltip title="Score 90: Ready-to-buy users. Smallest, most precise audience for direct outreach." placement="top">
                        <ToggleButton value="high">High</ToggleButton>
                      </Tooltip>
                    </ToggleButtonGroup>
                  </Box>

                  <Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      gutterBottom
                    >
                      Days Back
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mb: 1 }}
                    >
                      Filter contacts based on how recently their intent signal
                      was detected. A smaller value means more recent signals.
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      gutterBottom
                    >
                      {daysBack} days
                    </Typography>
                    <Slider
                      value={daysBack}
                      onChange={(_e, newValue) => setDaysBack(newValue as number)}
                      valueLabelDisplay="auto"
                      min={2}
                      max={10}
                      marks
                    />
                  </Box>
                </Stack>
              </AccordionDetails>
            </Accordion>

            {/* Business Section */}
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
                  <Typography fontWeight="medium">Business</Typography>
                  {businessFilterCount > 0 && (
                    <Chip label={`${businessFilterCount} applied`} size="small" />
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={2}>
                  <Box>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mb: 1,
                      }}
                    >
                      <Typography variant="subtitle2">
                        Business Keywords
                      </Typography>
                      <LoadingButton
                        size="small"
                        variant="outlined"
                        onClick={() => setShowKeywordGenerator(true)}
                        startIcon={<AutoAwesome />}
                      >
                        AI Generator
                      </LoadingButton>
                    </Box>
                    <Autocomplete
                      multiple
                      freeSolo
                      options={[]}
                      value={companyDescriptions}
                      onChange={(_e, newValue) =>
                        setCompanyDescriptions(newValue)
                      }
                      size="small"
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            label={option}
                            size="small"
                            {...getTagProps({ index })}
                            key={option}
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Business Keywords"
                          placeholder="Add keywords or use AI Generator..."
                          helperText="Enter keywords describing the business type or industry to find relevant audience segments"
                        />
                      )}
                    />
                  </Box>

                  <Autocomplete
                    multiple
                    freeSolo
                    options={industryOptions}
                    value={industries}
                    onChange={(_e, newValue) => setIndustries(newValue)}
                    size="small"
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          label={option}
                          size="small"
                          {...getTagProps({ index })}
                          key={option}
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Industries"
                        placeholder="Add..."
                      />
                    )}
                  />

                  <FormControl fullWidth size="small">
                    <InputLabel>Departments</InputLabel>
                    <Select
                      multiple
                      value={departments}
                      onChange={(e: SelectChangeEvent<string[]>) =>
                        setDepartments(e.target.value as string[])
                      }
                      label="Departments"
                      renderValue={(selected) => (
                        <Box
                          sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                        >
                          {selected.map((value) => (
                            <Chip
                              key={value}
                              label={
                                departmentOptions.find((d) => d.value === value)
                                  ?.label || value
                              }
                              size="small"
                            />
                          ))}
                        </Box>
                      )}
                    >
                      {departmentOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl fullWidth size="small">
                    <InputLabel>Seniority</InputLabel>
                    <Select
                      multiple
                      value={seniorities}
                      onChange={(e: SelectChangeEvent<string[]>) =>
                        setSeniorities(e.target.value as string[])
                      }
                      label="Seniority"
                      renderValue={(selected) => (
                        <Box
                          sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                        >
                          {selected.map((value) => (
                            <Chip
                              key={value}
                              label={
                                seniorityOptions.find((s) => s.value === value)
                                  ?.label || value
                              }
                              size="small"
                            />
                          ))}
                        </Box>
                      )}
                    >
                      {seniorityOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <Autocomplete
                    multiple
                    freeSolo
                    options={[]}
                    value={jobTitles}
                    onChange={(_e, newValue) => setJobTitles(newValue)}
                    size="small"
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          label={option}
                          size="small"
                          {...getTagProps({ index })}
                          key={option}
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Job Titles"
                        placeholder="Add..."
                      />
                    )}
                  />

                  <Autocomplete
                    multiple
                    freeSolo
                    options={[]}
                    value={companyNames}
                    onChange={(_e, newValue) => setCompanyNames(newValue)}
                    size="small"
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          label={option}
                          size="small"
                          {...getTagProps({ index })}
                          key={option}
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Company Names"
                        placeholder="Add..."
                      />
                    )}
                  />

                  <Autocomplete
                    multiple
                    freeSolo
                    options={[]}
                    value={companyDomains}
                    onChange={(_e, newValue) => setCompanyDomains(newValue)}
                    size="small"
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          label={option}
                          size="small"
                          {...getTagProps({ index })}
                          key={option}
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Company Domains"
                        placeholder="e.g., google.com"
                      />
                    )}
                  />

                  <FormControl fullWidth size="small">
                    <InputLabel>Employee Count</InputLabel>
                    <Select
                      multiple
                      value={employeeCounts}
                      onChange={(e: SelectChangeEvent<string[]>) =>
                        setEmployeeCounts(e.target.value as string[])
                      }
                      label="Employee Count"
                      renderValue={(selected) => (
                        <Box
                          sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                        >
                          {selected.map((value) => (
                            <Chip
                              key={value}
                              label={
                                employeeCountOptions.find(
                                  (e) => e.value === value
                                )?.label || value
                              }
                              size="small"
                            />
                          ))}
                        </Box>
                      )}
                    >
                      {employeeCountOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl fullWidth size="small">
                    <InputLabel>Company Revenue</InputLabel>
                    <Select
                      multiple
                      value={companyRevenues}
                      onChange={(e: SelectChangeEvent<string[]>) =>
                        setCompanyRevenues(e.target.value as string[])
                      }
                      label="Company Revenue"
                      renderValue={(selected) => (
                        <Box
                          sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                        >
                          {selected.map((value) => (
                            <Chip
                              key={value}
                              label={
                                companyRevenueOptions.find(
                                  (r) => r.value === value
                                )?.label || value
                              }
                              size="small"
                            />
                          ))}
                        </Box>
                      )}
                    >
                      {companyRevenueOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <Box>
                    <Autocomplete
                      multiple
                      freeSolo
                      options={sicCodeOptions}
                      value={sicCodes}
                      onChange={(_e, newValue) => setSicCodes(newValue)}
                      size="small"
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            label={option}
                            size="small"
                            {...getTagProps({ index })}
                            key={option}
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="SIC Codes"
                          placeholder="Add..."
                        />
                      )}
                    />
                    <Typography variant="caption" color="text.secondary">
                      <a
                        href="https://www.osha.gov/data/sic-manual"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: "inherit" }}
                      >
                        Look up SIC codes
                      </a>
                    </Typography>
                  </Box>

                  <Box>
                    <Autocomplete
                      multiple
                      freeSolo
                      options={naicsCodeOptions}
                      value={naicsCodes}
                      onChange={(_e, newValue) => setNaicsCodes(newValue)}
                      size="small"
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            label={option}
                            size="small"
                            {...getTagProps({ index })}
                            key={option}
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="NAICS Codes"
                          placeholder="Add..."
                        />
                      )}
                    />
                    <Typography variant="caption" color="text.secondary">
                      <a
                        href="https://www.naics.com/search/"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: "inherit" }}
                      >
                        Look up NAICS codes
                      </a>
                    </Typography>
                  </Box>
                </Stack>
              </AccordionDetails>
            </Accordion>

            {/* Financial Section */}
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
                  <Typography fontWeight="medium">Financial</Typography>
                  {financialFilterCount > 0 && (
                    <Chip label={`${financialFilterCount} applied`} size="small" />
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Income Range</InputLabel>
                    <Select
                      multiple
                      value={incomeRanges}
                      onChange={(e: SelectChangeEvent<string[]>) =>
                        setIncomeRanges(e.target.value as string[])
                      }
                      label="Income Range"
                      renderValue={(selected) => (
                        <Box
                          sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                        >
                          {selected.map((value) => (
                            <Chip
                              key={value}
                              label={
                                incomeRangeOptions.find(
                                  (i) => i.value === value
                                )?.label || value
                              }
                              size="small"
                            />
                          ))}
                        </Box>
                      )}
                    >
                      {incomeRangeOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl fullWidth size="small">
                    <InputLabel>Net Worth</InputLabel>
                    <Select
                      multiple
                      value={netWorth}
                      onChange={(e: SelectChangeEvent<string[]>) =>
                        setNetWorth(e.target.value as string[])
                      }
                      label="Net Worth"
                      renderValue={(selected) => (
                        <Box
                          sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                        >
                          {selected.map((value) => (
                            <Chip
                              key={value}
                              label={
                                netWorthOptions.find((n) => n.value === value)
                                  ?.label || value
                              }
                              size="small"
                            />
                          ))}
                        </Box>
                      )}
                    >
                      {netWorthOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl fullWidth size="small">
                    <InputLabel>Credit Rating</InputLabel>
                    <Select
                      multiple
                      value={creditRating}
                      onChange={(e: SelectChangeEvent<string[]>) =>
                        setCreditRating(e.target.value as string[])
                      }
                      label="Credit Rating"
                      renderValue={(selected) => (
                        <Box
                          sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                        >
                          {selected.map((value) => (
                            <Chip
                              key={value}
                              label={
                                creditRatingOptions.find(
                                  (c) => c.value === value
                                )?.label || value
                              }
                              size="small"
                            />
                          ))}
                        </Box>
                      )}
                    >
                      {creditRatingOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl fullWidth size="small">
                    <InputLabel>New Credit Range</InputLabel>
                    <Select
                      multiple
                      value={newCreditRange}
                      onChange={(e: SelectChangeEvent<string[]>) =>
                        setNewCreditRange(e.target.value as string[])
                      }
                      label="New Credit Range"
                      renderValue={(selected) => (
                        <Box
                          sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                        >
                          {selected.map((value) => (
                            <Chip
                              key={value}
                              label={
                                newCreditRangeOptions.find(
                                  (c) => c.value === value
                                )?.label || value
                              }
                              size="small"
                            />
                          ))}
                        </Box>
                      )}
                    >
                      {newCreditRangeOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl fullWidth size="small">
                    <InputLabel>Credit Card User</InputLabel>
                    <Select
                      multiple
                      value={creditCardUser}
                      onChange={(e: SelectChangeEvent<string[]>) =>
                        setCreditCardUser(e.target.value as string[])
                      }
                      label="Credit Card User"
                      renderValue={(selected) => (
                        <Box
                          sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                        >
                          {selected.map((value) => (
                            <Chip
                              key={value}
                              label={
                                creditCardUserOptions.find(
                                  (c) => c.value === value
                                )?.label || value
                              }
                              size="small"
                            />
                          ))}
                        </Box>
                      )}
                    >
                      {creditCardUserOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl fullWidth size="small">
                    <InputLabel>Investment</InputLabel>
                    <Select
                      multiple
                      value={investment}
                      onChange={(e: SelectChangeEvent<string[]>) =>
                        setInvestment(e.target.value as string[])
                      }
                      label="Investment"
                      renderValue={(selected) => (
                        <Box
                          sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                        >
                          {selected.map((value) => (
                            <Chip
                              key={value}
                              label={
                                investmentOptions.find((i) => i.value === value)
                                  ?.label || value
                              }
                              size="small"
                            />
                          ))}
                        </Box>
                      )}
                    >
                      {investmentOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl fullWidth size="small">
                    <InputLabel>Mortgage Amount</InputLabel>
                    <Select
                      multiple
                      value={mortgageAmount}
                      onChange={(e: SelectChangeEvent<string[]>) =>
                        setMortgageAmount(e.target.value as string[])
                      }
                      label="Mortgage Amount"
                      renderValue={(selected) => (
                        <Box
                          sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                        >
                          {selected.map((value) => (
                            <Chip
                              key={value}
                              label={
                                mortgageAmountOptions.find(
                                  (m) => m.value === value
                                )?.label || value
                              }
                              size="small"
                            />
                          ))}
                        </Box>
                      )}
                    >
                      {mortgageAmountOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl fullWidth size="small">
                    <InputLabel>Occupation Group</InputLabel>
                    <Select
                      multiple
                      value={occupationGroup}
                      onChange={(e: SelectChangeEvent<string[]>) =>
                        setOccupationGroup(e.target.value as string[])
                      }
                      label="Occupation Group"
                      renderValue={(selected) => (
                        <Box
                          sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                        >
                          {selected.map((value) => (
                            <Chip
                              key={value}
                              label={
                                occupationGroupOptions.find(
                                  (o) => o.value === value
                                )?.label || value
                              }
                              size="small"
                            />
                          ))}
                        </Box>
                      )}
                    >
                      {occupationGroupOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl fullWidth size="small">
                    <InputLabel>CRA Code</InputLabel>
                    <Select
                      multiple
                      value={craCode}
                      onChange={(e: SelectChangeEvent<string[]>) =>
                        setCraCode(e.target.value as string[])
                      }
                      label="CRA Code"
                      renderValue={(selected) => (
                        <Box
                          sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                        >
                          {selected.map((value) => (
                            <Chip
                              key={value}
                              label={
                                craCodeOptions.find((c) => c.value === value)
                                  ?.label || value
                              }
                              size="small"
                            />
                          ))}
                        </Box>
                      )}
                    >
                      {craCodeOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>
              </AccordionDetails>
            </Accordion>

            {/* Personal Section */}
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
                  <Typography fontWeight="medium">Personal</Typography>
                  {personalFilterCount > 0 && (
                    <Chip label={`${personalFilterCount} applied`} size="small" />
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Gender</InputLabel>
                    <Select
                      multiple
                      value={genders}
                      onChange={(e: SelectChangeEvent<string[]>) =>
                        setGenders(e.target.value as string[])
                      }
                      label="Gender"
                      renderValue={(selected) => (
                        <Box
                          sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                        >
                          {selected.map((value) => (
                            <Chip
                              key={value}
                              label={
                                genderOptions.find((g) => g.value === value)
                                  ?.label || value
                              }
                              size="small"
                            />
                          ))}
                        </Box>
                      )}
                    >
                      {genderOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <Box>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={useAgeFilter}
                          onChange={(e) => setUseAgeFilter(e.target.checked)}
                        />
                      }
                      label="Filter by Age"
                    />
                    {useAgeFilter && (
                      <Box sx={{ px: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          Age: {ageRange[0]} - {ageRange[1]}
                        </Typography>
                        <Slider
                          value={ageRange}
                          onChange={(_e, newValue) =>
                            setAgeRange(newValue as [number, number])
                          }
                          valueLabelDisplay="auto"
                          min={18}
                          max={80}
                        />
                      </Box>
                    )}
                  </Box>

                  <FormControl fullWidth size="small">
                    <InputLabel>Ethnicity</InputLabel>
                    <Select
                      multiple
                      value={ethnicity}
                      onChange={(e: SelectChangeEvent<string[]>) =>
                        setEthnicity(e.target.value as string[])
                      }
                      label="Ethnicity"
                      renderValue={(selected) => (
                        <Box
                          sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                        >
                          {selected.map((value) => (
                            <Chip
                              key={value}
                              label={
                                ethnicityOptions.find((e) => e.value === value)
                                  ?.label || value
                              }
                              size="small"
                            />
                          ))}
                        </Box>
                      )}
                    >
                      {ethnicityOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl fullWidth size="small">
                    <InputLabel>Language</InputLabel>
                    <Select
                      multiple
                      value={language}
                      onChange={(e: SelectChangeEvent<string[]>) =>
                        setLanguage(e.target.value as string[])
                      }
                      label="Language"
                      renderValue={(selected) => (
                        <Box
                          sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                        >
                          {selected.map((value) => (
                            <Chip
                              key={value}
                              label={
                                languageOptions.find((l) => l.value === value)
                                  ?.label || value
                              }
                              size="small"
                            />
                          ))}
                        </Box>
                      )}
                    >
                      {languageOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl fullWidth size="small">
                    <InputLabel>Education</InputLabel>
                    <Select
                      multiple
                      value={education}
                      onChange={(e: SelectChangeEvent<string[]>) =>
                        setEducation(e.target.value as string[])
                      }
                      label="Education"
                      renderValue={(selected) => (
                        <Box
                          sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                        >
                          {selected.map((value) => (
                            <Chip
                              key={value}
                              label={
                                educationOptions.find((e) => e.value === value)
                                  ?.label || value
                              }
                              size="small"
                            />
                          ))}
                        </Box>
                      )}
                    >
                      {educationOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl fullWidth size="small">
                    <InputLabel>Smoker</InputLabel>
                    <Select
                      multiple
                      value={smoker}
                      onChange={(e: SelectChangeEvent<string[]>) =>
                        setSmoker(e.target.value as string[])
                      }
                      label="Smoker"
                      renderValue={(selected) => (
                        <Box
                          sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                        >
                          {selected.map((value) => (
                            <Chip
                              key={value}
                              label={
                                smokerOptions.find((s) => s.value === value)
                                  ?.label || value
                              }
                              size="small"
                            />
                          ))}
                        </Box>
                      )}
                    >
                      {smokerOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>
              </AccordionDetails>
            </Accordion>

            {/* Housing Section */}
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
                  <Typography fontWeight="medium">Housing</Typography>
                  {housingFilterCount > 0 && (
                    <Chip label={`${housingFilterCount} applied`} size="small" />
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Homeowner Status</InputLabel>
                    <Select
                      multiple
                      value={homeowner}
                      onChange={(e: SelectChangeEvent<string[]>) =>
                        setHomeowner(e.target.value as string[])
                      }
                      label="Homeowner Status"
                      renderValue={(selected) => (
                        <Box
                          sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                        >
                          {selected.map((value) => (
                            <Chip key={value} label={value} size="small" />
                          ))}
                        </Box>
                      )}
                    >
                      <MenuItem value="homeowner">Homeowner</MenuItem>
                      <MenuItem value="renter">Renter</MenuItem>
                    </Select>
                  </FormControl>

                  <FormControl fullWidth size="small">
                    <InputLabel>Dwelling Type</InputLabel>
                    <Select
                      multiple
                      value={dwellingType}
                      onChange={(e: SelectChangeEvent<string[]>) =>
                        setDwellingType(e.target.value as string[])
                      }
                      label="Dwelling Type"
                      renderValue={(selected) => (
                        <Box
                          sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                        >
                          {selected.map((value) => (
                            <Chip
                              key={value}
                              label={
                                dwellingTypeOptions.find(
                                  (d) => d.value === value
                                )?.label || value
                              }
                              size="small"
                            />
                          ))}
                        </Box>
                      )}
                    >
                      {dwellingTypeOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <Typography variant="body2" color="text.secondary">
                    Year Built
                  </Typography>
                  <Stack direction="row" spacing={2}>
                    <TextField
                      size="small"
                      label="Min Year"
                      type="number"
                      value={homeYearBuiltMin}
                      onChange={(e) => setHomeYearBuiltMin(e.target.value)}
                      fullWidth
                    />
                    <TextField
                      size="small"
                      label="Max Year"
                      type="number"
                      value={homeYearBuiltMax}
                      onChange={(e) => setHomeYearBuiltMax(e.target.value)}
                      fullWidth
                    />
                  </Stack>

                  <Typography variant="body2" color="text.secondary">
                    Purchase Price
                  </Typography>
                  <Stack direction="row" spacing={2}>
                    <TextField
                      size="small"
                      label="Min Price"
                      type="number"
                      value={homePurchasePriceMin}
                      onChange={(e) => setHomePurchasePriceMin(e.target.value)}
                      fullWidth
                    />
                    <TextField
                      size="small"
                      label="Max Price"
                      type="number"
                      value={homePurchasePriceMax}
                      onChange={(e) => setHomePurchasePriceMax(e.target.value)}
                      fullWidth
                    />
                  </Stack>

                  <Typography variant="body2" color="text.secondary">
                    Purchase Year
                  </Typography>
                  <Stack direction="row" spacing={2}>
                    <TextField
                      size="small"
                      label="Min Year"
                      type="number"
                      value={homePurchaseYearMin}
                      onChange={(e) => setHomePurchaseYearMin(e.target.value)}
                      fullWidth
                    />
                    <TextField
                      size="small"
                      label="Max Year"
                      type="number"
                      value={homePurchaseYearMax}
                      onChange={(e) => setHomePurchaseYearMax(e.target.value)}
                      fullWidth
                    />
                  </Stack>

                  <FormControl fullWidth size="small">
                    <InputLabel>Estimated Home Value</InputLabel>
                    <Select
                      multiple
                      value={estimatedHomeValue}
                      onChange={(e: SelectChangeEvent<string[]>) =>
                        setEstimatedHomeValue(e.target.value as string[])
                      }
                      label="Estimated Home Value"
                      renderValue={(selected) => (
                        <Box
                          sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                        >
                          {selected.map((value) => (
                            <Chip
                              key={value}
                              label={
                                estimatedHomeValueOptions.find(
                                  (e) => e.value === value
                                )?.label || value
                              }
                              size="small"
                            />
                          ))}
                        </Box>
                      )}
                    >
                      {estimatedHomeValueOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>
              </AccordionDetails>
            </Accordion>

            {/* Family Section */}
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
                  <Typography fontWeight="medium">Family</Typography>
                  {familyFilterCount > 0 && (
                    <Chip label={`${familyFilterCount} applied`} size="small" />
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Married</InputLabel>
                    <Select
                      multiple
                      value={married}
                      onChange={(e: SelectChangeEvent<string[]>) =>
                        setMarried(e.target.value as string[])
                      }
                      label="Married"
                      renderValue={(selected) => (
                        <Box
                          sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                        >
                          {selected.map((value) => (
                            <Chip
                              key={value}
                              label={
                                yesNoOptions.find((y) => y.value === value)
                                  ?.label || value
                              }
                              size="small"
                            />
                          ))}
                        </Box>
                      )}
                    >
                      {yesNoOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl fullWidth size="small">
                    <InputLabel>Has Children</InputLabel>
                    <Select
                      multiple
                      value={children}
                      onChange={(e: SelectChangeEvent<string[]>) =>
                        setChildren(e.target.value as string[])
                      }
                      label="Has Children"
                      renderValue={(selected) => (
                        <Box
                          sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                        >
                          {selected.map((value) => (
                            <Chip
                              key={value}
                              label={
                                yesNoOptions.find((y) => y.value === value)
                                  ?.label || value
                              }
                              size="small"
                            />
                          ))}
                        </Box>
                      )}
                    >
                      {yesNoOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>
              </AccordionDetails>
            </Accordion>

            {/* Location Section */}
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
                  <Typography fontWeight="medium">Location</Typography>
                  {locationFilterCount > 0 && (
                    <Chip label={`${locationFilterCount} applied`} size="small" />
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={2}>
                  <Autocomplete
                    multiple
                    freeSolo
                    options={commonCities}
                    value={cities}
                    onChange={(_e, newValue) => setCities(newValue)}
                    size="small"
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          label={option}
                          size="small"
                          {...getTagProps({ index })}
                          key={option}
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Cities"
                        placeholder="Add..."
                      />
                    )}
                  />

                  <Autocomplete
                    multiple
                    options={usStates}
                    value={usStates.filter((s) => states.includes(s.value))}
                    onChange={(_e, newValue) =>
                      setStates(newValue.map((s) => s.value))
                    }
                    getOptionLabel={(option) => option.label}
                    isOptionEqualToValue={(option, value) =>
                      option.value === value.value
                    }
                    size="small"
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          label={option.label}
                          size="small"
                          {...getTagProps({ index })}
                          key={option.value}
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="States"
                        placeholder="Select..."
                      />
                    )}
                  />

                  <Autocomplete
                    multiple
                    freeSolo
                    options={[]}
                    value={zipCodes}
                    onChange={(_e, newValue) => setZipCodes(newValue)}
                    size="small"
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          label={option}
                          size="small"
                          {...getTagProps({ index })}
                          key={option}
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Zip Codes"
                        placeholder="Add..."
                      />
                    )}
                  />
                </Stack>
              </AccordionDetails>
            </Accordion>

            {/* Contact Section */}
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
                  <Typography fontWeight="medium">Contact</Typography>
                  {contactFilterCount > 0 && (
                    <Chip label={`${contactFilterCount} applied`} size="small" />
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={1}>
                  {contactNotNullsOptions.map((option) => (
                    <Paper
                      key={option.value}
                      variant="outlined"
                      sx={{ px: 2, py: 1.5, borderRadius: 2 }}
                    >
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Typography>{option.label}</Typography>
                        <Switch
                          checked={contactNotNulls.includes(option.value)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setContactNotNulls([
                                ...contactNotNulls,
                                option.value,
                              ]);
                            } else {
                              setContactNotNulls(
                                contactNotNulls.filter(
                                  (v) => v !== option.value
                                )
                              );
                            }
                          }}
                        />
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              </AccordionDetails>
            </Accordion>

            <Divider sx={{ my: 2 }} />

            {/* Action Buttons */}
            <Stack spacing={1}>
              <LoadingButton
                variant="contained"
                fullWidth
                onClick={handlePreview}
                loading={previewLoading}
                startIcon={<Search />}
                disabled={!audienceName.trim() || !hasFilters()}
              >
                {hasPreview ? "Update Preview" : "Preview Audience"}
              </LoadingButton>
              {hasPreview && (
                <LoadingButton
                  variant="outlined"
                  fullWidth
                  onClick={handleRefreshPreview}
                  loading={refreshPreviewLoading}
                  startIcon={<Refresh />}
                >
                  Refresh Preview
                </LoadingButton>
              )}
              {hasPreview && !isSaved && (
                <LoadingButton
                  variant="contained"
                  color="primary"
                  fullWidth
                  onClick={handleSave}
                  loading={saveLoading}
                  startIcon={<Save />}
                  disabled={!audienceName.trim() || !hasFilters()}
                >
                  Save Audience
                </LoadingButton>
              )}
              {isSaved && (
                <LoadingButton
                  variant="outlined"
                  color="error"
                  fullWidth
                  onClick={handleDelete}
                  loading={deleteLoading}
                  startIcon={<Delete />}
                >
                  Delete Audience
                </LoadingButton>
              )}
            </Stack>

            {/* Refresh Settings - shown after save */}
            {isSaved && (
              <Box sx={{ mt: 3 }}>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Auto Refresh
                  </Typography>
                  <Chip
                    size="small"
                    label={refreshInterval === "none" ? "Off" : refreshInterval === "3days" ? "Every 3 Days" : refreshInterval.charAt(0).toUpperCase() + refreshInterval.slice(1)}
                    color={refreshInterval === "none" ? "default" : "primary"}
                  />
                </Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mb: 2 }}
                >
                  {refreshInterval === "none"
                    ? "Auto refresh is disabled"
                    : `Refreshes ${refreshInterval === "3days" ? "every 3 days" : refreshInterval}, max ${maxRecordsPerRefresh ?? "unlimited"} records`}
                </Typography>
                <LoadingButton
                  variant="outlined"
                  fullWidth
                  size="small"
                  onClick={() => setRefreshSettingsDialogOpen(true)}
                  startIcon={<Refresh />}
                >
                  Configure Refresh
                </LoadingButton>
              </Box>
            )}

            {!hasFilters() && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 1, display: "block", textAlign: "center" }}
              >
                Add filters to preview audience
              </Typography>
            )}
            {hasPreview && !isSaved && (
              <Typography
                variant="caption"
                color="warning.main"
                sx={{ mt: 1, display: "block", textAlign: "center" }}
              >
                Preview only - click Save to persist
              </Typography>
            )}
          </Box>

          {/* Right Panel - Preview */}
          <Box sx={{ flex: 1, display: "flex", flexDirection: "column", p: 2 }}>
            {loadingAudience ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  flex: 1,
                  color: "text.secondary",
                }}
              >
                <Typography variant="h6">Loading...</Typography>
              </Box>
            ) : !hasPreview ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  flex: 1,
                  color: "text.secondary",
                }}
              >
                <PersonSearch sx={{ fontSize: 64, mb: 2, opacity: 0.5 }} />
                <Typography variant="h6">Preview Contacts</Typography>
                <Typography variant="body2">
                  Configure filters and click Preview to see matching contacts
                </Typography>
              </Box>
            ) : audienceStatus === "building" || pollingForStatus ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  flex: 1,
                  color: "text.secondary",
                }}
              >
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    border: 3,
                    borderColor: "primary.main",
                    borderTopColor: "transparent",
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite",
                    mb: 2,
                    "@keyframes spin": {
                      "0%": { transform: "rotate(0deg)" },
                      "100%": { transform: "rotate(360deg)" },
                    },
                  }}
                />
                <Typography variant="h6">Building Audience...</Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Processing your filters. This can take up to a minute.
                </Typography>
                {pollAttempts > 0 && (
                  <Typography variant="caption" color="text.disabled">
                    Checking... ({pollAttempts}/{MAX_POLL_ATTEMPTS})
                  </Typography>
                )}
              </Box>
            ) : previewContacts.length === 0 ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  flex: 1,
                  color: "text.secondary",
                }}
              >
                <Typography variant="h6">No Results</Typography>
                <Typography variant="body2">
                  Try adjusting your filters
                </Typography>
              </Box>
            ) : (
              <>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Typography variant="subtitle1">
                    {totalRecords.toLocaleString()} contacts found
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                      <InputLabel>Import Size</InputLabel>
                      <Select
                        value={importSize}
                        label="Import Size"
                        onChange={(e: SelectChangeEvent) => setImportSize(e.target.value)}
                        disabled={!isSaved || importLoading}
                      >
                        <MenuItem value="25">25</MenuItem>
                        <MenuItem value="50">50</MenuItem>
                        <MenuItem value="75">75</MenuItem>
                        <MenuItem value="100">100</MenuItem>
                        <MenuItem value="250">250</MenuItem>
                        <MenuItem value="500">500</MenuItem>
                        <MenuItem value="1000">1,000</MenuItem>
                        <MenuItem value="5000">5,000</MenuItem>
                        <MenuItem value="10000">10,000</MenuItem>
                        <MenuItem value="all">All ({totalRecords.toLocaleString()})</MenuItem>
                      </Select>
                    </FormControl>
                    <Tooltip
                      title={
                        !isSaved
                          ? "Save the audience first before importing"
                          : ""
                      }
                    >
                      <span>
                        <LoadingButton
                          variant="contained"
                          size="small"
                          onClick={() => handleImport()}
                          loading={importLoading}
                          startIcon={<Check />}
                          disabled={!isSaved}
                          sx={{ height: 40 }}
                        >
                          Import{importSize !== "all" ? ` ${parseInt(importSize, 10).toLocaleString()}` : ` All`}
                        </LoadingButton>
                      </span>
                    </Tooltip>
                    <LoadingButton
                      variant="outlined"
                      size="small"
                      onClick={handleExport}
                      loading={exportLoading}
                      startIcon={<Download />}
                      disabled={!hasFilters()}
                      sx={{ height: 40 }}
                    >
                      Export CSV
                    </LoadingButton>
                  </Box>
                </Box>

                <TableContainer
                  component={Paper}
                  variant="outlined"
                  sx={{ flex: 1 }}
                >
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Title</TableCell>
                        <TableCell>Company</TableCell>
                        <TableCell>Location</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Phone</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {previewContacts.map((contact, idx) => (
                        <TableRow key={idx} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {contact.firstName}{" "}
                              {contact.lastName
                                ? contact.lastName.slice(0, 3) +
                                  "*".repeat(Math.max(0, contact.lastName.length - 3))
                                : ""}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{
                                maxWidth: 200,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {contact.positionTitle || "-"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {contact.companyName
                                ? contact.companyName.slice(0, 3) +
                                  "*".repeat(Math.max(0, contact.companyName.length - 3))
                                : "-"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {contact.location || "-"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {contact.email ? (
                              <Chip
                                label="Available"
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                            ) : (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                -
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            {contact.phoneNumber ? (
                              <Chip
                                label="Available"
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                            ) : (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                -
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Pagination */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mt: 2,
                    pt: 2,
                    borderTop: 1,
                    borderColor: "divider",
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Showing {(currentPage - 1) * pageSize + 1}-
                    {Math.min(currentPage * pageSize, totalRecords)} of{" "}
                    {totalRecords.toLocaleString()} results
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <IconButton
                      size="small"
                      onClick={() => handlePageChange(1)}
                      disabled={currentPage === 1 || previewLoading}
                    >
                      <FirstPage />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1 || previewLoading}
                    >
                      <NavigateBefore />
                    </IconButton>
                    <Typography variant="body2" sx={{ mx: 1 }}>
                      Page {currentPage} of {totalPages.toLocaleString()}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages || previewLoading}
                    >
                      <NavigateNext />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handlePageChange(totalPages)}
                      disabled={currentPage === totalPages || previewLoading}
                    >
                      <LastPage />
                    </IconButton>
                  </Box>
                </Box>
              </>
            )}
          </Box>
        </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <LoadingButton
          onClick={onClose}
          disabled={previewLoading || importLoading}
        >
          Cancel
        </LoadingButton>
      </DialogActions>

      {/* AI Keyword Generator Dialog */}
      <Dialog
        open={showKeywordGenerator}
        onClose={() => {
          if (!generatingKeywords) {
            setShowKeywordGenerator(false);
            setKeywordGeneratorInput("");
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <AutoAwesome color="primary" />
              AI Keyword Generator
            </Box>
            <IconButton
              size="small"
              onClick={() => {
                if (!generatingKeywords) {
                  setShowKeywordGenerator(false);
                  setKeywordGeneratorInput("");
                }
              }}
              disabled={generatingKeywords}
            >
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Describe your business type or industry, and AI will generate
            optimized search keywords for finding relevant audience segments.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            rows={3}
            label="Business Description"
            placeholder="e.g., Independent boutique hotels, SaaS companies providing HR software, E-commerce businesses selling organic skincare..."
            value={keywordGeneratorInput}
            onChange={(e) => setKeywordGeneratorInput(e.target.value)}
            disabled={generatingKeywords}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <LoadingButton
            onClick={() => {
              setShowKeywordGenerator(false);
              setKeywordGeneratorInput("");
            }}
            disabled={generatingKeywords}
          >
            Cancel
          </LoadingButton>
          <LoadingButton
            variant="contained"
            onClick={handleGenerateKeywords}
            loading={generatingKeywords}
            disabled={!keywordGeneratorInput.trim()}
            startIcon={<AutoAwesome />}
          >
            Generate Keywords
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* AI Intent Keyword Generator Dialog */}
      <Dialog
        open={showIntentKeywordGenerator}
        onClose={() => {
          if (!generatingIntentKeywords) {
            setShowIntentKeywordGenerator(false);
            setIntentKeywordGeneratorInput("");
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <AutoAwesome color="primary" />
              AI Intent Keyword Generator
            </Box>
            <IconButton
              size="small"
              onClick={() => {
                if (!generatingIntentKeywords) {
                  setShowIntentKeywordGenerator(false);
                  setIntentKeywordGeneratorInput("");
                }
              }}
              disabled={generatingIntentKeywords}
            >
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Describe the topic, product, or service you want to target, and AI
            will generate optimized intent keywords for finding relevant
            audience signals.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            rows={3}
            label="Topic Description"
            placeholder="e.g., email marketing software, cloud storage solutions, project management tools..."
            value={intentKeywordGeneratorInput}
            onChange={(e) => setIntentKeywordGeneratorInput(e.target.value)}
            disabled={generatingIntentKeywords}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <LoadingButton
            onClick={() => {
              setShowIntentKeywordGenerator(false);
              setIntentKeywordGeneratorInput("");
            }}
            disabled={generatingIntentKeywords}
          >
            Cancel
          </LoadingButton>
          <LoadingButton
            variant="contained"
            onClick={handleGenerateIntentKeywords}
            loading={generatingIntentKeywords}
            disabled={!intentKeywordGeneratorInput.trim()}
            startIcon={<AutoAwesome />}
          >
            Generate Keywords
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* Refresh Settings Dialog */}
      <RefreshSettingsDialog
        open={refreshSettingsDialogOpen}
        onClose={() => setRefreshSettingsDialogOpen(false)}
        getToken={getToken}
        companyId={companyId}
        listId={listId}
        currentRefreshInterval={refreshInterval}
        currentMaxRecordsPerRefresh={maxRecordsPerRefresh}
        currentEmailNotificationEnabled={emailNotificationEnabled}
        currentEmailNotificationRecipients={emailNotificationRecipients}
        currentEmailNotificationFormat={emailNotificationFormat}
        onSave={handleRefreshSettingsSave}
      />

      {/* Segment Search Dialog */}
      <SegmentSearchDialog
        open={segmentSearchDialogOpen}
        onClose={() => setSegmentSearchDialogOpen(false)}
        getToken={getToken}
        companyId={companyId}
        selectedSegments={selectedSegments}
        onSegmentsChange={setSelectedSegments}
      />

      {/* Previous Audiences Dialog */}
      <PreviousAudiencesDialog
        open={previousAudiencesDialogOpen}
        onClose={() => setPreviousAudiencesDialogOpen(false)}
        getToken={getToken}
        companyId={companyId}
        currentListId={listId}
        onSelectAudience={loadFiltersFromPreviousAudience}
      />
    </Dialog>
  );
};

export default AudienceBuilderDialog;
